import React from 'react'

import {
  Body2,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  Heading3,
  Image,
  JustifyContent,
  Overflow,
  ResponsiveValue,
  Strong,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { WhatsNewBodySegment } from '../../../config'

/** Responsive image zoom factor(s) — a single scale number, or a per-breakpoint array. */
type ImageZoom = number | ResponsiveValue<number>

/** Responsive `object-position` value(s) — a single CSS string, or a per-breakpoint array. */
type ImagePosition = string | ResponsiveValue<string>

type CoverImageProps = React.ComponentProps<typeof Image> & {
  imageZoom?: ImageZoom
  imagePosition?: ImagePosition
  imageZoomOrigin?: string
}

// Each card frames its image independently. `imagePosition` shifts the visible crop (e.g. card 1's
// portrait shows the plate at the bottom rather than the centre leaves); `imageZoom` scales the image
// about `imageZoomOrigin` (e.g. card 3 scales about the top so its box drops clear of the floating
// pills). The image column clips any overflow. All default to the natural cover framing when omitted.
const CoverImage = withCitrusTheme<CoverImageProps>(
  ({
    imageZoom: _imageZoom,
    imagePosition: _imagePosition,
    imageZoomOrigin: _o,
    ...imageProps
  }) => <Image {...imageProps} />,
  (theme, props) => [
    {
      objectFit: 'cover',
      width: '100%',
      height: '100%',
      display: 'block',
      transformOrigin: props.imageZoomOrigin ?? 'center bottom',
    },
    props.imagePosition
      ? theme.responsiveStyle(
          Array.isArray(props.imagePosition) ? props.imagePosition : [props.imagePosition],
          ['objectPosition'],
        )
      : {},
    props.imageZoom
      ? theme.responsiveStyle(
          (Array.isArray(props.imageZoom) ? props.imageZoom : [props.imageZoom]).map((zoom) =>
            zoom == null ? null : `scale(${zoom})`,
          ) as ResponsiveValue<string>,
          ['transform'],
        )
      : {},
  ],
)

// <picture> wrapper so the card image serves WebP with a JPEG fallback (matching the
// HowItWorksSection pattern). Absolutely positioned to fill the image column so the card height stays
// driven by the text/min-height (not the image's intrinsic size) — the cover image then tracks the
// card height regardless of the source aspect ratio (e.g. card 1's portrait plate image).
const CoverPicture = withCitrusTheme('picture', () => ({
  position: 'absolute',
  inset: 0,
  display: 'block',
  width: '100%',
  height: '100%',
}))

// The design's 8px card radius is outside Citrus's RadiusScale (max 1.5 → 3px),
// so it is applied via withCitrusTheme rather than the borderRadius prop. `flex: 1 1 auto` lets the
// card fill its equal-height grid cell (all cards match the tallest — see CWCWhatsNewSection's grid).
const CardSurface = withCitrusTheme(Box, () => ({
  borderRadius: '8px',
  flex: '1 1 auto',
}))

// Exact Figma typography — the design sizes/line-heights and the `#1A2026` ink
// colour aren't expressible via Citrus's size scale / Color tokens, so they are
// set here. Card heading: Heading 5 (16/24) on mobile, Heading 3 (24/32) from tablet.
const CardHeading = withCitrusTheme(Heading3, (theme) => [
  { color: '#1A2026' },
  theme.responsiveStyle(['16px', null, '24px'], ['fontSize']),
  theme.responsiveStyle(['24px', null, '32px'], ['lineHeight']),
])

// Card body: 14/20 on mobile, 16/24 from tablet; grey `#41505C`.
const CardBody = withCitrusTheme(Body2, (theme) => [
  { color: '#41505C' },
  theme.responsiveStyle(['14px', null, '16px'], ['fontSize']),
  theme.responsiveStyle(['20px', null, '24px'], ['lineHeight']),
])

// Text column fills the space beside the image and shrinks so the copy wraps
// (flex items default to min-width:auto, which would push the card past the viewport).
const TextColumn = withCitrusTheme(Box, () => ({
  flex: '1 1 0',
  minWidth: 0,
}))

// Forces a line break within the body copy on mobile only. A zero-height block interrupts the inline
// flow so the following text drops to a new line; `display: none` from tablet up keeps the copy inline.
const MobileBreak = withCitrusTheme('span', (theme) => [
  { height: 0 },
  theme.responsiveStyle(['block', null, 'none'], ['display']),
])

export type CWCFeatureCardProps = {
  title: string
  body: WhatsNewBodySegment[]
  /** Card image with a WebP source and a JPEG fallback for older browsers. */
  image: { webp: string; jpg: string }
  /** Alt text describing the card image for assistive tech. Defaults to `""` (decorative). */
  imageAlt?: string
  /**
   * Optional responsive zoom for the cover image (scale factor), so a card can enlarge its subject to
   * match Figma. Anchored at `imageZoomOrigin`. Defaults to no zoom (scale 1) when omitted.
   */
  imageZoom?: ImageZoom
  /**
   * Transform-origin for `imageZoom` (e.g. `center top` drops the subject downward as it scales).
   * Defaults to `center bottom`.
   */
  imageZoomOrigin?: string
  /**
   * Optional responsive `object-position` for the cover image, so a card can shift which part of the
   * image is shown (e.g. frame a portrait image on its plate). Defaults to centre when omitted.
   */
  imagePosition?: ImagePosition
  /** Optional decorative overlay rendered over the image (e.g. card-3 floating pills). */
  imageOverlay?: React.ReactNode
  /**
   * Optional decorative badge overhanging the top-left corner (e.g. `<CWCNewBadge />`). Off by default;
   * pass a node to render it. Kept as a slot so the NEW badge can be re-enabled in one line.
   */
  badge?: React.ReactNode
}

export const CWCFeatureCard = ({
  title,
  body,
  image,
  imageAlt = '',
  imageZoom,
  imageZoomOrigin,
  imagePosition,
  imageOverlay,
  badge,
}: CWCFeatureCardProps) => (
  <Box
    position={DisplayPosition.Relative}
    width="100%"
    height="100%"
    display={Display.Flex}
    flexDirection={FlexDirection.Column}
  >
    <CardSurface
      display={Display.Flex}
      flexDirection={FlexDirection.Row}
      bg={Color.White}
      overflowX={Overflow.Hidden}
      overflowY={Overflow.Hidden}
      minHeight={['114px', null, '150px']}
    >
      <Box
        position={DisplayPosition.Relative}
        width={['112px', null, '230px']}
        flexShrink={0}
        overflowX={Overflow.Hidden}
        overflowY={Overflow.Hidden}
      >
        <CoverPicture>
          <source srcSet={image.webp} type="image/webp" />
          <CoverImage
            src={image.jpg}
            alt={imageAlt}
            imageZoom={imageZoom}
            imageZoomOrigin={imageZoomOrigin}
            imagePosition={imagePosition}
          />
        </CoverPicture>
        {imageOverlay}
      </Box>
      <TextColumn
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.Center}
        paddingH={[4, null, 8]}
        paddingV={3}
      >
        <CardHeading>{title}</CardHeading>
        <CardBody>
          {body.map((segment, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={index}>
              {segment.breakBefore && <MobileBreak aria-hidden />}
              {segment.strong ? (
                <Strong color={Color.Primary_600}>{segment.text}</Strong>
              ) : (
                segment.text
              )}
            </React.Fragment>
          ))}
        </CardBody>
      </TextColumn>
    </CardSurface>
    {/* Optional decorative badge, painted after the card so it overhangs the top-left corner.
        The current sticker asset has no transparent canvas bleed (the image box is the visible
        sticker), so these offsets are the raw Figma frame position relative to the card's top-left
        corner: x/y -11/-13 mobile, -21/-15 tablet+desktop (see ui-design.md). */}
    {badge && (
      <Box
        position={DisplayPosition.Absolute}
        top={['-13px', null, '-15px']}
        left={['-11px', null, '-21px']}
      >
        {badge}
      </Box>
    )}
  </Box>
)
